import React from "react";
import { formatInstant } from "../../lib/calendar-date";
import { RENTAL_RESULT_LABEL } from "../../lib/rental-inspections";
import type { RentalItemAnswer, RentalItemResult, RentalOverall } from "../../types/rental-inspection";

/**
 * What the rental inspection register, its page and the draft page share
 * (#815 Inspections). Moved out of RentalInspections.tsx when its split pane
 * became a register and a page; the readings are unchanged.
 */

export type InspectionType = "pre_delivery" | "post_return";

/**
 * The one grade vocabulary: Pass / Minor / Fail — the structured inspection's
 * own (api#861). Major is collapsed into Fail (operator, 2026-10-01).
 */
export type Grade = RentalOverall;

export interface DisplayItem {
  key: string;
  label: string;
  /** null: a grade this screen cannot read. Never guessed as a Pass. */
  result: RentalItemResult | null;
  notes: string;
}

export interface Inspection {
  id: string;
  asset_id: string;
  asset_name: string;
  asset_tag: string;
  order_id: string;
  /** null: a type this screen does not know (the column is free text). Never guessed. */
  type: InspectionType | null;
  /** The tenant's day AND time: an inspection is an instant. */
  when: string;
  performed_by: string;
  /** null: no grade can be read. */
  overall: Grade | null;
  /** Written by the structured checklist (api#861), not the older shape. */
  structured: boolean;
  family_label: string | null;
  work_order_id: string | null;
  items: DisplayItem[];
  /**
   * The photo keys the record names (`photo_urls` then `damage_photos`).
   * `undefined` when the row carries neither column, so a reader cannot filter
   * by them; `[]` when it carries them and they are empty. Never one for the other.
   */
  photo_keys: string[] | undefined;
}

/**
 * What `GET /surface-rentals/inspections` (and `/inspections/:id`) returns:
 * `rental_inspections.*` plus `asset_number` and `asset_name`. It joins no
 * order or customer.
 */
export interface InspectionApiRow {
  id: string;
  asset_id: string;
  order_id?: string | null;
  asset_name?: string | null;
  asset_number?: string | null;
  inspection_type?: string | null;
  inspection_date?: string | null;
  inspector_name?: string | null;
  condition?: string | null;
  checklist?: unknown;
  checklist_version?: string | null;
  checklist_family?: string | null;
  item_results?: RentalItemAnswer[] | null;
  overall_result?: string | null;
  work_order_id?: string | null;
  photo_urls?: unknown;
  damage_photos?: unknown;
}

export const DASH = "—";

type Skin = { label: string; fill: string; rail: string };

/** Exhaustive over the three grades. Unknown is a slate dash with a transparent rail, never Pass. */
const GRADE_SKIN: Record<Grade, Skin> = {
  pass: { label: "Pass", fill: "bg-fi-signal-green", rail: "border-l-fi-green" },
  minor: { label: "Minor", fill: "bg-fi-signal-amber", rail: "border-l-fi-amber" },
  fail: { label: "Fail", fill: "bg-fi-signal-red", rail: "border-l-fi-red" },
};
const ITEM_SKIN: Record<RentalItemResult, string> = {
  pass: "bg-fi-signal-green",
  minor: "bg-fi-signal-amber",
  fail: "bg-fi-signal-red",
  na: "bg-fi-signal-slate",
};
const UNKNOWN_SKIN: Skin = { label: DASH, fill: "bg-fi-signal-slate", rail: "border-l-transparent" };
export const gradeSkin = (g: Grade | null): Skin => (g ? GRADE_SKIN[g] : UNKNOWN_SKIN);

function Badge({ label, fill, prefix }: { label: string; fill: string; prefix?: string }) {
  return (
    <span
      className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${fill}`}
    >
      {prefix}
      {label}
    </span>
  );
}
export const GradeBadge = ({ grade, prefix }: { grade: Grade | null; prefix?: string }) => {
  const s = gradeSkin(grade);
  return <Badge label={s.label} fill={s.fill} prefix={prefix} />;
};
export const ItemBadge = ({ result }: { result: RentalItemResult | null }) =>
  result ? <Badge label={RENTAL_RESULT_LABEL[result]} fill={ITEM_SKIN[result]} /> : <Badge label={DASH} fill={UNKNOWN_SKIN.fill} />;

export const TYPE_LABEL: Record<InspectionType, string> = {
  pre_delivery: "Pre-Delivery",
  post_return: "Post-Return",
};

/** The type as an outline chip: it is what kind of inspection, not how it went, so it claims no status hue. */
export function TypeChip({ type }: { type: InspectionType | null }) {
  return (
    <span className="inline-flex rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-[0.68rem] font-bold uppercase leading-none tracking-[0.06em] text-fieldiq-text">
      {type ? TYPE_LABEL[type] : DASH}
    </span>
  );
}

/**
 * THE OLDER SHAPE — rows written before api#861, and by screens still sending
 * it. Its grades were pass / minor_defect / major_defect / fail; Major is read
 * as Fail. Its `condition` (rental_assets' vocabulary) is the fallback when no
 * checklist grade can be read.
 */
const LEGACY_GRADE: Record<string, RentalItemResult> = {
  pass: "pass",
  minor_defect: "minor",
  major_defect: "fail",
  fail: "fail",
};
const GRADE_FOR_CONDITION: Record<string, Grade> = {
  excellent: "pass",
  good: "pass",
  fair: "minor",
  poor: "fail",
  damaged: "fail",
};
const isGrade = (g: unknown): g is Grade => g === "pass" || g === "minor" || g === "fail";
const ITEM_RESULTS = new Set<string>(["pass", "minor", "fail", "na"]);

function worstOf(items: DisplayItem[]): Grade | null {
  if (items.some((i) => i.result === null)) return null;
  const rs = items.map((i) => i.result).filter((r) => r !== "na");
  if (rs.length === 0) return null;
  if (rs.includes("fail")) return "fail";
  if (rs.includes("minor")) return "minor";
  return "pass";
}

const stringsOf = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.trim() !== "") : []);

/** `undefined` only when the row has neither photo column at all. */
function photoKeysOf(row: InspectionApiRow): string[] | undefined {
  if (row.photo_urls === undefined && row.damage_photos === undefined) return undefined;
  return Array.from(new Set([...stringsOf(row.photo_urls), ...stringsOf(row.damage_photos)]));
}

/** `timeZone` is the tenant's: `inspection_date` is an instant, shown in that zone. */
export function normalizeInspection(row: InspectionApiRow, timeZone: string | null): Inspection {
  const structured = row.checklist_version != null && Array.isArray(row.item_results);
  let items: DisplayItem[];
  let overall: Grade | null;
  if (structured) {
    items = (row.item_results ?? []).map((a) => ({
      key: a.item,
      label: a.label ?? a.item,
      result: ITEM_RESULTS.has(a.result) ? a.result : null,
      notes: a.notes ?? "",
    }));
    // The server's own derivation, not a recomputation.
    overall = isGrade(row.overall_result) ? row.overall_result : null;
  } else {
    const legacy = Array.isArray(row.checklist)
      ? (row.checklist as { item_name?: unknown; result?: unknown; notes?: unknown }[]).filter(
          (it) => it && typeof it === "object" && typeof it.item_name === "string",
        )
      : [];
    items = legacy.map((it, n) => ({
      key: `legacy-${n}`,
      label: String(it.item_name),
      result: typeof it.result === "string" && LEGACY_GRADE[it.result] ? LEGACY_GRADE[it.result] : null,
      notes: typeof it.notes === "string" ? it.notes : "",
    }));
    overall = items.length > 0 ? worstOf(items) : GRADE_FOR_CONDITION[row.condition ?? ""] ?? null;
  }
  return {
    id: row.id,
    asset_id: row.asset_id,
    asset_name: row.asset_name ?? DASH,
    asset_tag: row.asset_number ?? DASH,
    order_id: row.order_id ?? "",
    type: row.inspection_type === "pre_delivery" || row.inspection_type === "post_return" ? row.inspection_type : null,
    when: formatInstant(row.inspection_date ?? null, timeZone, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) ?? DASH,
    performed_by: row.inspector_name ?? DASH,
    overall,
    structured,
    family_label: row.checklist_family ?? null,
    work_order_id: row.work_order_id ?? null,
    items,
    photo_keys: photoKeysOf(row),
  };
}
